import React, { useState } from 'react';
import StatusBadge from './StatusBadge';

/**
 * SyncStatusPanel - Always-visible panel showing sync status and debugging info
 */
const SyncStatusPanel = ({
  isConnected,
  googleEmail,
  lastSyncAt,
  syncResults,
  syncing,
  error,
  onSync,
  counts,
  searchScope,
  permissions,
  warnings,
  onDismissWarnings,
  hideSyncButton = false,
  hideStats = false,
  className = ''
}) => {
  const [showDetails, setShowDetails] = useState(false);

  // Format relative time
  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  // Determine connection status badge
  const getConnectionBadge = () => {
    if (!isConnected) {
      return <StatusBadge variant="error">Not Connected</StatusBadge>;
    }
    if (syncing) {
      return <StatusBadge variant="syncing">Syncing...</StatusBadge>;
    }
    if (error) {
      return <StatusBadge variant="warning">Error</StatusBadge>;
    }
    return <StatusBadge variant="success">Connected</StatusBadge>;
  };

  return (
    <div className={`sync-status-panel ${className}`.trim()} role="region" aria-label="Sync Status">
      {/* Main Status Row */}
      <div className="status-panel-header">
        <div className="status-panel-connection">
          {getConnectionBadge()}
          {isConnected && googleEmail && (
            <span className="status-email">{googleEmail}</span>
          )}
        </div>

        <div className="status-panel-actions">
          {!hideSyncButton && (
            <button
              className="btn btn-primary btn-small"
              onClick={onSync}
              disabled={syncing || !isConnected}
              aria-busy={syncing}
            >
              {syncing ? (
                <>
                  <span className="btn-spinner"></span>
                  Syncing...
                </>
              ) : (
                'Sync Now'
              )}
            </button>
          )}
          <button
            className="btn btn-ghost btn-icon"
            onClick={() => setShowDetails(!showDetails)}
            aria-expanded={showDetails}
            title={showDetails ? 'Hide details' : 'Show details'}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points={showDetails ? "18 15 12 9 6 15" : "6 9 12 15 18 9"}></polyline>
            </svg>
          </button>
        </div>
      </div>

      {/* Stats Row */}
      {!hideStats && isConnected && counts && (
        <div className="sync-stats-grid" aria-label="Sync statistics">
          <div className="stat-item">
            <span className="stat-value">{counts.total || 0}</span>
            <span className="stat-label">Meetings</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{counts.with_transcript || 0}</span>
            <span className="stat-label">Transcripts</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{counts.with_notes || 0}</span>
            <span className="stat-label">With Notes</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{counts.pending || 0}</span>
            <span className="stat-label">Pending</span>
          </div>
        </div>
      )}

      {/* Last Sync Info */}
      {isConnected && (
        <div className="sync-meta-row">
          <span className="sync-meta-item">
            <span className="meta-label">Last synced:</span>
            <span className="meta-value">{formatRelativeTime(lastSyncAt)}</span>
          </span>
        </div>
      )}

      {/* Warnings */}
      {warnings && warnings.length > 0 && (
        <div className="sync-warnings" role="alert">
          {onDismissWarnings && (
            <div className="sync-warnings-header">
              <span className="sync-warnings-title">Sync Notes</span>
              <button
                type="button"
                className="sync-warnings-dismiss"
                onClick={onDismissWarnings}
                aria-label="Dismiss sync warnings"
              >
                Dismiss
              </button>
            </div>
          )}
          {warnings.map((warning, index) => (
            <div key={index} className="sync-warning">
              <StatusBadge variant="warning" size="small">Note</StatusBadge>
              <span>{warning.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="sync-error" role="alert">
          <StatusBadge variant="error" size="small">Error</StatusBadge>
          <span>{error}</span>
        </div>
      )}

      {/* Collapsible Details */}
      {showDetails && isConnected && (
        <div className="status-panel-details">
          <div className="detail-section">
            <h4>Search Scope</h4>
            <p className="detail-value">{searchScope || 'Entire Google Drive (meeting notes)'}</p>
          </div>

          <div className="detail-section">
            <h4>API Permissions</h4>
            <div className="permissions-list">
              {permissions ? (
                <>
                  <span className={`permission-item ${permissions.meet ? 'granted' : 'missing'}`}>
                    {permissions.meet ? 'Yes' : 'No'} Google Meet
                  </span>
                  <span className={`permission-item ${permissions.drive ? 'granted' : 'missing'}`}>
                    {permissions.drive ? 'Yes' : 'No'} Google Drive
                  </span>
                  <span className={`permission-item ${permissions.docs ? 'granted' : 'missing'}`}>
                    {permissions.docs ? 'Yes' : 'No'} Google Docs
                  </span>
                  <span className={`permission-item ${permissions.calendar ? 'granted' : 'missing'}`}>
                    {permissions.calendar ? 'Yes' : 'No'} Google Calendar
                  </span>
                  <span className={`permission-item ${permissions.gmail ? 'granted' : 'missing'}`}>
                    {permissions.gmail ? 'Yes' : 'No'} Gmail
                  </span>
                </>
              ) : (
                <span className="no-permissions">Permissions not available</span>
              )}
            </div>
          </div>

          {syncResults && syncResults.search_locations && (
            <div className="detail-section">
              <h4>Data Sources</h4>
              <ul className="data-sources-list">
                {syncResults.search_locations.map((location, index) => (
                  <li key={index}>{location}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="detail-section">
            <h4>Troubleshooting</h4>
            <ul className="troubleshooting-list">
              <li>Transcripts take 15-45 minutes to become available after a meeting ends</li>
              <li>Enable "Take notes for me" in Google Meet settings for automatic notes</li>
              <li>Only meetings you organized or were invited to will appear</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default SyncStatusPanel;

